import { useState, useEffect } from 'react';

/**
 * EventFeedback - Shows post-event mitigation breakdown
 * 
 * Non-judgmental language:
 * - "Impact reduced"
 * - "Recovery accelerated"
 * - "System absorbed remaining cost"
 */
export default function EventFeedback({ life }) {
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    if (!life || !life.eventHistory) return;

    // Get last 3 events with mitigation breakdown
    const recent = life.eventHistory
      .filter(e => e.mitigationBreakdown)
      .slice(-3)
      .reverse();

    setRecentEvents(recent);
  }, [life?.eventHistory?.length]);

  if (recentEvents.length === 0) return null;

  return (
    <div className="bg-white/5 rounded-xl p-6 border border-white/10 backdrop-blur-sm">
      <h3 className="text-lg font-mono mb-4 text-gray-300">Recent Events</h3>
      
      <div className="space-y-4">
        {recentEvents.map((event, index) => (
          <EventCard key={`${event.id}-${index}`} event={event} />
        ))}
      </div>
    </div>
  );
}

function EventCard({ event }) {
  const breakdown = event.mitigationBreakdown;
  if (!breakdown) return null;

  const totalDamage = breakdown.systemicDamage + breakdown.collectiveDamage + breakdown.personalDamage;
  const totalPossible = event.baseDamage || 1; // Safety: prevent division by zero
  const absorbed = breakdown.absorbed || 0;
  const mitigationPercent = (absorbed / totalPossible) * 100;

  return (
    <div className="bg-black/30 rounded-lg p-4 border border-white/5">
      {/* Event name */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-white font-mono text-sm">{event.name}</span>
        <span className="text-xs text-gray-500">{getZoneIcon(event.zone)}</span>
      </div>

      {/* 3-Layer Breakdown */}
      <div className="space-y-2 mb-3">
        <DamageLayer
          label="Systemic Force"
          damage={breakdown.systemicDamage}
          total={totalPossible}
          color="red"
          description="Unavoidable"
        />
        <DamageLayer
          label="Collective Impact"
          damage={breakdown.collectiveDamage}
          total={totalPossible}
          color="yellow"
          description="Partially mitigable"
        />
        <DamageLayer
          label="Personal Exposure"
          damage={breakdown.personalDamage}
          total={totalPossible}
          color="green"
          description="Your choices mattered"
        />
      </div>

      {/* Summary */}
      <div className="pt-3 border-t border-white/10">
        {mitigationPercent > 50 ? (
          <p className="text-sm text-green-400">
            ✓ Impact reduced by {Math.floor(mitigationPercent)}%
          </p>
        ) : mitigationPercent > 20 ? (
          <p className="text-sm text-blue-400">
            ↓ System absorbed {Math.floor(absorbed)} damage
          </p>
        ) : (
          <p className="text-sm text-gray-400">
            → Mostly unavoidable ({Math.floor((breakdown.systemicDamage / totalDamage) * 100)}% systemic)
          </p>
        )}
      </div>
    </div>
  );
}

function DamageLayer({ label, damage, total, color, description }) {
  const percent = (damage / (total || 1)) * 100;
  
  const colorClasses = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500'
  };

  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-500">{Math.floor(damage)}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="text-xs text-gray-600 mt-1">{description}</div>
    </div>
  );
}

function getZoneIcon(zone) {
  const icons = {
    home: '🏠',
    auto: '🚗',
    health: '💚',
    liability: '⚖️'
  };
  return icons[zone] || '●';
}

